import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Loader2, Printer, AlertTriangle, Target, CheckCircle, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Lab 10 — Capstone + Responsible AI Checklist (manual steps 86–94).
 * Integrates work from llm.foundryailabs.io, codefoundry.sh and this platform.
 * Mostly a structured template with 3 LLM-assisted sections:
 *   • problem_plan   (step 87 — definitions, requirements, assumptions, risks)
 *   • exec_summary   (step 91 — polish while preserving facts)
 *   • rai_reflection (step 92+93 — RAI reflection seed from checklist state)
 */
const RAI_ITEMS = [
  { id: 'hallucination', label: 'Hallucination check — I verified facts in model outputs against trusted sources.' },
  { id: 'bias',          label: 'Bias check — I considered whose perspective is missing or over-represented.' },
  { id: 'privacy',       label: 'Privacy (DPDP Act) — no personal data of real people was used without consent.' },
  { id: 'copyright',     label: 'Copyright — generated media & reused material respect licence terms.' },
  { id: 'citation',      label: 'Citation — I named every AI tool and dataset used, and declared AI assistance.' },
  { id: 'integrity',     label: 'Academic integrity — human judgement, not AI alone, drove key decisions.' },
];

export default function LabCapstone() {
  const [project, setProject] = useState('');
  const [plan, setPlan] = useState(null);
  const [exec, setExec] = useState('');
  const [execPolished, setExecPolished] = useState(null);
  const [checklist, setChecklist] = useState(Object.fromEntries(RAI_ITEMS.map(i => [i.id, { pass: false, note: '' }])));
  const [reflection, setReflection] = useState('');
  const [reflectionSeed, setReflectionSeed] = useState(null);
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');

  const call = async (path, body) => {
    const r = await fetch(`${API}${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.detail || `Request failed (${r.status})`);
    return data;
  };

  const genPlan = async () => {
    setLoading('plan'); setError('');
    try {
      const data = await call('/api/labs/10/ask', { kind: 'problem_plan', context: { project } });
      setPlan(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(''); }
  };

  const polishExec = async () => {
    setLoading('exec'); setError('');
    try {
      const data = await call('/api/labs/10/ask', { kind: 'exec_summary', context: { draft: exec } });
      setExecPolished(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(''); }
  };

  const seedReflection = async () => {
    setLoading('reflection'); setError('');
    try {
      const checklistSummary = RAI_ITEMS.map(i => ({ item: i.label, ...checklist[i.id] }));
      const data = await call('/api/labs/10/ask', { kind: 'rai_reflection', context: { project, checklist: checklistSummary } });
      setReflectionSeed(data);
      if (data.reflection && !reflection.trim()) setReflection(data.reflection);
    } catch (e) { setError(e.message); }
    finally { setLoading(''); }
  };

  const toggle = (id, patch) => setChecklist(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const exportPDF = () => {
    const w = window.open('', '_blank'); if (!w) return;
    const safe = s => (s || '').toString().replace(/</g, '&lt;');
    const execFinal = execPolished?.polished || exec;
    const checkRows = RAI_ITEMS.map(i => `<tr><td>${safe(i.label)}</td><td style="text-align:center">${checklist[i.id].pass ? '✓' : '✗'}</td><td>${safe(checklist[i.id].note)}</td></tr>`).join('');
    w.document.write(`<html><head><title>Lab 10 — Capstone Deliverable</title>
      <style>body{font-family:-apple-system,sans-serif;padding:28px;max-width:780px;margin:0 auto;color:#111;}
      h1,h2{line-height:1.25;margin-top:1.4em;}
      table{border-collapse:collapse;width:100%;margin-top:8px;}
      td,th{border:1px solid #cbd5e1;padding:6px 10px;font-size:12.5px;text-align:left;}
      pre{background:#f8fafc;border:1px solid #e2e8f0;padding:12px;border-radius:8px;white-space:pre-wrap;}
      ul{margin:6px 0;padding-left:1.2em;}</style></head><body>
      <h1>Lab 10 — Capstone + Responsible AI</h1>
      <p><strong>Mini-project:</strong> ${safe(project)}</p>
      ${plan ? `
        <h2>Problem & Plan (llm.foundryailabs.io)</h2>
        <p>${safe(plan.problem)}</p>
        <strong>Requirements:</strong><ul>${(plan.requirements||[]).map(r=>`<li>${safe(r)}</li>`).join('')}</ul>
        <strong>Assumptions:</strong><ul>${(plan.assumptions||[]).map(r=>`<li>${safe(r)}</li>`).join('')}</ul>
        <strong>Risks:</strong><ul>${(plan.risks||[]).map(r=>`<li>${safe(r)}</li>`).join('')}</ul>
        <strong>Prompt plan:</strong>
        <ul>
          <li><strong>LLM:</strong> ${safe(plan.prompt_plan?.llm)}</li>
          <li><strong>CodeFoundry:</strong> ${safe(plan.prompt_plan?.codefoundry)}</li>
          <li><strong>Studio:</strong> ${safe(plan.prompt_plan?.studio)}</li>
        </ul>` : ''}
      <h2>Executive summary</h2>
      <pre>${safe(execFinal)}</pre>
      <h2>Responsible-AI checklist</h2>
      <table><thead><tr><th>Item</th><th>Pass</th><th>Note</th></tr></thead><tbody>${checkRows}</tbody></table>
      <h2>Reflection</h2>
      <pre>${safe(reflection)}</pre>
      </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  const Step = ({ n, title, children }) => (
    <section style={{ marginTop: 20, padding: 18, border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 28, height: 28, borderRadius: 14, background: '#ec4899', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13 }}>{n}</div>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h3>
      </div>
      {children}
    </section>
  );

  const Btn = ({ onClick, disabled, children, slot, dataTestId, variant = 'primary' }) => (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={dataTestId}
      style={{
        padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
        background: variant === 'primary' ? '#ec4899' : 'transparent',
        color: variant === 'primary' ? '#fff' : '#ec4899',
        border: variant === 'primary' ? 'none' : '1px solid #ec4899',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
      {loading === slot ? <Loader2 size={13} className="spin-l" /> : children}
    </button>
  );

  const txt = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, lineHeight: 1.5, resize: 'vertical' };

  return (
    <div style={{ padding: '1.5rem 1.5rem 3rem', maxWidth: 1000, margin: '0 auto' }} data-testid="lab-10">
      <Link to="/labs" style={{ color: '#ec4899', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, textDecoration: 'none', marginBottom: 6 }}><ArrowLeft size={14} /> Back to Labs</Link>
      <h1 style={{ margin: '4px 0 2px', fontSize: 24, fontWeight: 600 }}>Lab 10 — Capstone + Responsible AI</h1>
      <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>Manual steps 86–94. Plan → build → analyse → present → polish → RAI checklist → reflection → PDF.</p>

      {error && <div data-testid="lab-error" style={{ marginTop: 10, padding: '8px 12px', background: '#fee2e2', border: '1px solid #ef4444', borderRadius: 8, color: '#b91c1c', fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}><AlertTriangle size={13} /> {error}</div>}

      <Step n={1} title="Pick your mini-project (Step 86)">
        <input value={project} onChange={e => setProject(e.target.value)} placeholder="e.g. campus energy monitor / attendance dashboard / water-usage analyzer" style={txt} data-testid="lab-project" />
      </Step>

      <Step n={2} title="Problem definition & prompt plan (Step 87 — llm.foundryailabs.io)">
        <Btn onClick={genPlan} disabled={!project.trim() || !!loading} slot="plan" dataTestId="lab-plan-btn"><Target size={13} /> Generate problem + prompt plan</Btn>
        {plan && (
          <div style={{ marginTop: 10, padding: 12, background: '#fdf2f8', border: '1px solid #fbcfe8', borderRadius: 8, fontSize: 13 }} data-testid="lab-plan">
            <p style={{ margin: '0 0 8px' }}>{plan.problem}</p>
            <strong>Requirements:</strong><ul style={{ margin: '2px 0 8px', paddingLeft: 20 }}>{(plan.requirements || []).map((r, k) => <li key={k}>{r}</li>)}</ul>
            <strong>Assumptions:</strong><ul style={{ margin: '2px 0 8px', paddingLeft: 20 }}>{(plan.assumptions || []).map((r, k) => <li key={k}>{r}</li>)}</ul>
            <strong>Risks:</strong><ul style={{ margin: '2px 0 8px', paddingLeft: 20 }}>{(plan.risks || []).map((r, k) => <li key={k}>{r}</li>)}</ul>
            {plan.prompt_plan && (
              <>
                <strong>Prompt plan for each platform:</strong>
                <ul style={{ margin: '2px 0 0', paddingLeft: 20 }}>
                  <li><strong>LLM:</strong> {plan.prompt_plan.llm} <a href="https://llm.foundryailabs.io" target="_blank" rel="noreferrer" style={{ color: '#ec4899' }}><ExternalLink size={10} /></a></li>
                  <li><strong>CodeFoundry:</strong> {plan.prompt_plan.codefoundry} <a href="https://codefoundry.sh" target="_blank" rel="noreferrer" style={{ color: '#ec4899' }}><ExternalLink size={10} /></a></li>
                  <li><strong>Studio:</strong> {plan.prompt_plan.studio}</li>
                </ul>
              </>
            )}
          </div>
        )}
      </Step>

      <Step n={3} title="Cross-platform work (Steps 88–90)">
        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#4b5563', lineHeight: 1.8 }}>
          <li>Step 88 — Build the prototype on <a href="https://codefoundry.sh" target="_blank" rel="noreferrer">codefoundry.sh</a>. Save your project link.</li>
          <li>Step 89 — Analyze your sample data. Verify ONE claim manually.</li>
          <li>Step 90 — Create your project visual (Lab 8) and 5-slide deck (Lab 9) <Link to="/labs/image" style={{ color: '#ec4899' }}>→ open Lab 8</Link> · <Link to="/labs/presentation" style={{ color: '#ec4899' }}>→ open Lab 9</Link></li>
        </ol>
      </Step>

      <Step n={4} title="Polish the executive summary (Step 91)">
        <textarea rows={5} value={exec} onChange={e => setExec(e.target.value)} placeholder="Paste your draft executive summary (facts only — the agent won't invent)." style={txt} data-testid="lab-exec-draft" />
        <div style={{ marginTop: 8 }}>
          <Btn onClick={polishExec} disabled={!exec.trim() || !!loading} slot="exec" variant="secondary" dataTestId="lab-exec-btn"><Sparkles size={13} /> Polish without changing facts</Btn>
        </div>
        {execPolished && (
          <div style={{ marginTop: 10, padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 13 }} data-testid="lab-exec-polished">
            <strong>Polished:</strong>
            <p style={{ margin: '4px 0 8px' }}>{execPolished.polished}</p>
            {execPolished.changes?.length > 0 && (
              <>
                <strong>Biggest edits:</strong>
                <ul style={{ margin: '2px 0 0', paddingLeft: 20 }}>{execPolished.changes.map((c, k) => <li key={k}>{c}</li>)}</ul>
              </>
            )}
          </div>
        )}
      </Step>

      <Step n={5} title="Responsible-AI checklist (Step 92)">
        <div style={{ display: 'grid', gap: 10 }}>
          {RAI_ITEMS.map(item => (
            <label key={item.id} data-testid={`rai-${item.id}`} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 10, background: checklist[item.id].pass ? '#f0fdf4' : '#fff7ed', border: `1px solid ${checklist[item.id].pass ? '#86efac' : '#fdba74'}`, borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={checklist[item.id].pass} onChange={e => toggle(item.id, { pass: e.target.checked })} data-testid={`rai-check-${item.id}`} style={{ marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div>{item.label}</div>
                <input value={checklist[item.id].note} onChange={e => toggle(item.id, { note: e.target.value })} placeholder="One-line note (how you checked)" data-testid={`rai-note-${item.id}`} style={{ ...txt, marginTop: 4, fontSize: 12, padding: '5px 8px' }} />
              </div>
            </label>
          ))}
        </div>
      </Step>

      <Step n={6} title="Reflection (Step 93)">
        <Btn onClick={seedReflection} disabled={!project.trim() || !!loading} slot="reflection" variant="secondary" dataTestId="lab-reflection-btn"><Sparkles size={13} /> Generate reflection seed from your checklist</Btn>
        {reflectionSeed?.flags?.length > 0 && (
          <div style={{ marginTop: 8, padding: 10, background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, fontSize: 12 }} data-testid="lab-reflection-flags">
            <strong>⚠ Still needs your attention:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>{reflectionSeed.flags.map((f, k) => <li key={k}>{f}</li>)}</ul>
          </div>
        )}
        <textarea rows={6} value={reflection} onChange={e => setReflection(e.target.value)} placeholder="Write a one-page reflection: which AI tool for each task, where human judgment was essential." style={{ ...txt, marginTop: 10 }} data-testid="lab-reflection-text" />
      </Step>

      <Step n={7} title="Export capstone deliverable (Step 94)">
        <Btn onClick={exportPDF} disabled={!project.trim()} slot="pdf" variant="secondary" dataTestId="lab-export-pdf"><Printer size={13} /> Save capstone PDF</Btn>
        <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center', color: '#059669', fontSize: 12 }}>
          <CheckCircle size={13} /> Upload the PDF to your Google Drive Lab10 folder per the manual's submission guide.
        </div>
      </Step>

      <style>{`.spin-l{animation:spin-l .9s linear infinite}@keyframes spin-l{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
